<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import Button from 'primevue/button'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import CustomerForm from '@/views/customers/CustomerForm.vue'
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { useAuthStore } from '@/stores/auth'
import { customerApi, type Customer, type CustomerPayload } from '@/services/customers'

/**
 * Edición de cliente.
 *
 * El guardado es parcial: se manda el formulario entero, pero el backend solo
 * toca los campos presentes. Vaciar un campo sí lo borra, porque en ese caso se
 * envía `null` explícito y no la ausencia de la clave.
 */

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const allowed = computed(() => authStore.hasPermission('customers.manage'))
const saving = ref(false)
const serverErrors = ref<Record<string, string[]>>({})
const saveError = ref<string | null>(null)
const loadError = ref<string | null>(null)

const customer = ref<Customer | null>(null)
const loading = ref(false)

const id = computed(() => {
  const parsed = Number.parseInt(String(route.params.id ?? ''), 10)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
})

/** El formulario se alimenta de lo que devuelve la API, con `null` incluidos. */
const formValues = computed(() => {
  if (customer.value === null) return undefined
  return {
    name: customer.value.name,
    document: customer.value.document,
    email: customer.value.email,
    phone: customer.value.phone,
    company: customer.value.company,
    city: customer.value.city,
    address: customer.value.address,
    source: customer.value.source,
  }
})

/**
 * Volver al listado **con los filtros con los que se llegó**.
 *
 * El listado lleva sus filtros en la URL —para eso está `useFiltrosEnUrl`—, así que al
 * abrir esta vista se los lleva en la query. Sin devolvérselos, quien filtra, abre una
 * ficha y vuelve se encuentra la lista entera: el filtro se aplicó y desapareció.
 *
 * «Volver» empuja una entrada **nueva**, así que el historial no salva: la entrada anterior,
 * la que tenía los filtros, deja de ser la que se está usando.
 */
const { destino: destinoListado } = useVolverAlListado('customers.index')

function messageFor(cause: unknown, fallback: string): string {
  const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
  return maybeAxios.response?.data?.error?.message ?? fallback
}

function extractServerErrors(cause: unknown): Record<string, string[]> {
  const maybeAxios = cause as {
    validationErrors?: Record<string, string[]>
    response?: { data?: { error?: { details?: { fields?: Record<string, string[]> } } } }
  }
  return maybeAxios.validationErrors ?? maybeAxios.response?.data?.error?.details?.fields ?? {}
}

async function load(): Promise<void> {
  saveError.value = null
  loadError.value = null

  if (id.value === null) {
    loadError.value = 'Ese cliente no existe en el directorio.'
    return
  }

  loading.value = true

  try {
    const { data } = await customerApi.get(id.value)
    customer.value = data.data
  } catch (cause: unknown) {
    customer.value = null
    loadError.value = messageFor(cause, 'No se pudo cargar el cliente.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(id, load)

/** Vuelve al detalle del cliente, o al listado si no se llegó a cargar. */
function goBack(): void {
  if (customer.value === null) {
    void router.push(destinoListado.value)
    return
  }
  void router.push({ name: 'customers.show', params: { id: String(customer.value.id) } })
}

async function handleSubmit(payload: CustomerPayload): Promise<void> {
  if (customer.value === null) return

  saving.value = true
  serverErrors.value = {}
  saveError.value = null

  try {
    await customerApi.update(customer.value.id, payload)
    await router.push({ name: 'customers.show', params: { id: String(customer.value.id) } })
  } catch (cause: unknown) {
    serverErrors.value = extractServerErrors(cause)
    // Un 422 ya se explica campo a campo; un aviso global taparía el error real.
    saveError.value = Object.keys(serverErrors.value).length > 0 ? null : messageFor(cause, 'No se pudieron guardar los cambios.')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div class="space-y-6 animate-fade-in">
    <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div class="min-w-0">
        <h1 class="truncate text-2xl font-semibold text-surface-900 dark:text-surface-100">
          Editar cliente
        </h1>
        <p
          v-if="customer"
          class="mt-1 text-sm text-surface-500 dark:text-surface-400"
        >
          {{ customer.name }}
        </p>
      </div>
      <Button
        label="Cancelar"
        icon="pi pi-arrow-left"
        severity="secondary"
        :disabled="saving"
        @click="goBack"
      />
    </header>

    <Message
      v-if="!allowed"
      severity="warn"
      :closable="false"
      variant="simple"
      role="alert"
    >
      No tienes permiso de gestión de clientes, así que no puedes modificar registros.
    </Message>

    <Message
      v-if="loadError"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ loadError }}
    </Message>

    <Message
      v-if="saveError"
      severity="error"
      :closable="false"
      variant="simple"
      role="alert"
    >
      {{ saveError }}
    </Message>

    <AppCard v-if="loading">
      <div class="space-y-4">
        <Skeleton
          v-for="index in 5"
          :key="index"
          height="1.25rem"
        />
      </div>
    </AppCard>

    <CustomerForm
      v-else-if="customer && allowed"
      :values="formValues"
      submit-label="Guardar cambios"
      :loading="saving"
      :server-errors="serverErrors"
      @submit="handleSubmit"
      @cancel="goBack"
    />
  </div>
</template>
